<template>
  <v-form ref="form">
    <v-row>
      <v-col cols="12">
        <p class="primary--text form__title mb-8">
          DADOS DA APÓLICE
        </p>
      </v-col>
      <v-col cols="12" sm="4" class="pr-sm-5">
        <p class="form__label primary--text mb-2">
          Número da Apólice
        </p>
        <p class="body-2 primary--text mb-7">
          {{ currentPolicy.numero }}
        </p>
      </v-col>
      <v-col cols="12" sm="4" class="pr-sm-5">
        <p class="form__label primary--text mb-2">
          Seguradora
        </p>
        <p class="body-2 primary--text mb-7">
          {{ currentPolicy.seguradora.nome }}
        </p>
      </v-col>
      <v-col cols="12" sm="4">
        <p class="form__label primary--text mb-2">
          Tipo de emissão
        </p>
        <p class="body-2 primary--text mb-7">
          {{ currentPolicy.tipo_emissao.nome }} -
          {{ currentPolicy.submodalidade.nome }}
        </p>
      </v-col>
    </v-row>
    <v-row class="pb-8">
      <v-col cols="12" sm="4" class="pr-sm-5">
        <p class="form__label primary--text mb-2">
          Grupo Econômico
        </p>
        <p class="body-2 primary--text mb-7">
          {{ currentPolicy.tomador.grupo.nome }}
        </p>
      </v-col>
      <v-col cols="12" sm="4" class="pr-sm-5">
        <p class="form__label primary--text mb-2">
          Início de Vigência
        </p>
        <p class="body-2 primary--text mb-7">
          {{ formatDateToDDMMYYYY(currentPolicy.inicio_vigencia) }}
        </p>
      </v-col>
      <v-col cols="12" sm="4">
        <p class="form__label primary--text mb-2">
          Importância Segurada
        </p>
        <p class="body-2 primary--text mb-7">
          {{ asMoneyString(currentPolicy.valor_importancia_segurada) }}
        </p>
      </v-col>
      <v-col cols="12" sm="4" class="pr-sm-5">
        <p class="form__label primary--text mb-2">
          Tomador Matriz/Filial
        </p>
        <p class="body-2 primary--text mb-7">
          {{ currentPolicy.tomador.nome_fantasia }}
        </p>
      </v-col>
      <v-col cols="12" sm="4" class="pr-sm-5">
        <p class="form__label primary--text mb-2">
          Fim de Vigência
        </p>
        <p class="body-2 primary--text mb-7">
          {{ formatDateToDDMMYYYY(currentPolicy.fim_vigencia) }}
        </p>
      </v-col>
      <v-col cols="12" sm="4">
        <p class="form__label primary--text mb-2">
          Prêmio Total Emitido
        </p>
        <p class="body-2 primary--text mb-7">
          {{ asMoneyString(currentPolicy.valor_premio) }}
        </p>
      </v-col>
      <v-col cols="12" sm="4" class="pr-sm-5" v-if="enableCreationDate">
        <p class="form__label primary--text mb-2">
          Data da Solicitação
        </p>
        <p class="body-2 primary--text mb-7">
          {{ formatDateToDDMMYYYY(currentPolicy.solicitacoes_endossos[0].criada_em) }}
        </p>
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
    </v-row>
    <v-row v-if="canApproveRequest || canReproveRequest">
      <v-col cols="12" class="mb-5">
        <v-row class="yellow px-2 py-3 endorsement-form__disclaimer">
          <p class="caption primary-text font-weight-bold mb-0">
            Somente aprove ou reprove a solicitação de endosso quando tiver o
            retorno da seguradora. Caso haja necessidade de informações ou
            documentos adicionais, utilize a sessão de registro de críticas para
            solicitá-los ao cliente.
          </p>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-if="showCriticism" class="mb-5">
      <BaseDataTableSection
        :show-button="currentRequestEndorsement.acoes.pode_registrar_critica"
        text-information="São pontos de desconformidade ou discordância na solicitação de endosso, criados pelo cliente e verificados junto a seguradora, podendo ou não impedir a emissão da mesma."
      >
        <BaseDataTable
          :headers="headerCriticism"
          :items="[currentRequestEndorsement.status_critica]"
          no-data-text="Nenhuma Crítica foi encontrada"
          hide-default-footer
          hide-default-header
          class="table-header-end base__datatable--border"
        >
          <template v-slot:[`item.critica`]>
            <div class="font-weight-bold mb-2">
              <v-icon color="primary" size="13"> fa-exclamation-circle </v-icon>
              Crítica
            </div>
            <div>
              {{ currentRequestEndorsement.quantidade_criticas_pendentes }}
              crítica(s) pendente(s)
            </div>
          </template>
          <template v-slot:[`item.chat`]>
            <div class="font-weight-bold mb-2">
              <v-icon color="primary" size="14"> far fa-comment </v-icon>
              Chat
            </div>
            <div>
              {{ currentRequestEndorsement.quantidade_chats_abertos }} chat(s)
              aberto(s)
            </div>
          </template>
          <template v-slot:[`item.acoes`]>
            <BaseButton
              v-html="'VISUALIZAR'"
              class="secondary"
              @click="redirectCriticism"
            />
          </template>
        </BaseDataTable>
      </BaseDataTableSection>
    </v-row>
    <v-row>
      <v-col cols="12">
        <p class="primary--text form__title mb-5">
          DADOS DO ENDOSSO
        </p>
      </v-col>
      <v-col cols="12" v-if="form.tem_solicitacao_cancelamento">
        <img src="@/assets/ic_warning.svg" class="px-1" />
        <p class="subtitle-2 primary--text d-inline-block">
          {{ textTooltip }}
        </p>
      </v-col>
      <v-col cols="12" sm="4" class="pr-sm-5" v-if="!isRequestEndorsement">
        <BaseFormInput
          id-element="numberEndorsement"
          placeholder="Inserir"
          v-model="form.numero"
          required
          :rules="[requiredInput]"
          label-text="N° do Endosso"
          :disabled="disableForm"
        />
      </v-col>
      <v-col cols="12" sm="8">
        <BaseFormSelect
          id-element="typeInsuredEndorsement"
          placeholder="Selecionar"
          v-model="formTipo"
          :items="listTypeEndorsement"
          :disabled="isEdit || disableForm || disableTypeEndorsement"
          required
          :rules="[requiredInput]"
          label-text="Tipo de Endosso"
          item-text="nome"
          item-value="id"
          :return-object="true"
        />
      </v-col>
    </v-row>
    <v-row class="pb-8">
      <v-col
        cols="12"
        sm="4"
        class="pr-sm-5"
        v-if="enableInputsTypeEffectInsuredAmount"
      >
        <BaseMoney
          id-element="newInsuredAmountEndorsement"
          placeholder="Inserir"
          v-model="form.valor_importancia_segurada"
          required
          :rules="[requiredInput]"
          label-text="Importância Segurada (Nova)"
          :disabled="disableForm"
        />
      </v-col>
      <v-col
        cols="12"
        sm="4"
        class="pr-sm-5"
        v-if="enableInputsTypeEffectEndTerm"
      >
        <BaseFormInput
          id-element="newEndTermEndorsement"
          placeholder="DD/MM/YYYY"
          v-model="form.novo_fim_vigencia"
          required
          :rules="[requiredInput, dateTermValid, newDateTermLimit]"
          label-text="Término de Vigência (Novo)"
          v-facade="maskDate"
          :disabled="disableForm"
        />
      </v-col>
      <v-col
        cols="12"
        sm="4"
        class="pr-sm-5"
        v-if="enableInputsCancel && !isRequestEndorsement"
      >
        <BaseFormInput
          id-element="dateEndTermEndorsement"
          placeholder="DD/MM/YYYY"
          v-model="form.cancelado_em"
          required
          :rules="[requiredInput, dateTermValid]"
          label-text="Data do Cancelamento"
          v-facade="maskDate"
          :disabled="disableForm"
        />
      </v-col>
      <v-col
        cols="12"
        sm="4"
        class="pr-sm-5"
        v-if="enableInputsCancel && listCancelItems.length > 1"
      >
        <BaseFormSelect
          id-element="reasonCancelEndorsement"
          placeholder="Inserir"
          v-model="form.motivo_cancelamento"
          :items="listCancelItems"
          item-text="descricao"
          item-value="id"
          :return-object="true"
          required
          :rules="[requiredInput]"
          label-text="Motivo do Cancelamento"
          :disabled="disableForm"
        />
      </v-col>
      <v-col cols="12" sm="4" v-if="enableInputNumberCancelEndorsement">
        <BaseFormSelect
          id-element="numberCancelEndorsement"
          placeholder="Inserir"
          :items="itemsEndorsement"
          item-text="numero"
          item-value="numero"
          :return-object="false"
          v-model="form.numero_endosso_cancelamento"
          required
          :rules="[requiredInput]"
          label-text="N° Endosso Cancelado"
          :disabled="disableForm || disableTypeEndorsement"
        />
      </v-col>
      <v-col cols="12" sm="4" v-if="disableInputNumberCancelEndorsement">
        <BaseFormInput
          id-element="numberCancelEndorsement"
          placeholder="Inserir"
          disabled
          v-model="form.numero_endosso_cancelamento"
          label-text="N° Endosso Cancelado"
        />
      </v-col>
      <v-col
        cols="12"
        sm="8"
        md="4"
        v-if="enableOtherReasons"
        :class="paddingInputMoreInfo"
      >
        <BaseFormTextArea
          id-element="reasonCancelEndorsement"
          placeholder="Inserir"
          v-model="form.informacoes_adicionais"
          required
          :rules="[requiredInput, maxLengthTextArea]"
          :label-text="labelCancelInput"
          :disabled="disableForm"
        />
      </v-col>
      <v-col cols="12" sm="8" md="4" v-if="!isCreate && !isRequestEndorsement">
        <BaseFormInput
          id-element="originEndorsement"
          :placeholder="origemCadastroPlaceholder"
          v-model="form.origem_cadastro.nome"
          label-text="Origem Cadastro"
          disabled
        />
      </v-col>
      <v-col cols="12" sm="4" class="pr-sm-5" v-if="enableInputsEndEndorsement">
        <BaseFormInput
          id-element="dateEndTermEndorsement"
          placeholder="DD/MM/YYYY"
          v-model="form.baixada_em"
          required
          :rules="[requiredInput, dateTermValid]"
          label-text="Data da Baixa"
          v-facade="maskDate"
          :disabled="disableForm"
        />
      </v-col>
      <v-col cols="12" sm="4" class="pr-sm-5" v-if="enableInputsEndEndorsement">
        <BaseFormSelect
          id-element="reasonEndEndorsement"
          placeholder="Selecione"
          v-model="form.motivo_baixa"
          :items="listCancelItems"
          item-text="descricao"
          item-value="id"
          :return-object="true"
          required
          :rules="[requiredInput]"
          label-text="Motivo da Baixa"
          :disabled="disableForm"
        />
      </v-col>
      <v-col
        cols="12"
        sm="4"
        class="pr-sm-5"
        v-if="enableInputsTypeEffectInsuredAmountDontRequired"
      >
        <BaseMoney
          id-element="newInsuredAmountEndorsement"
          placeholder="Inserir"
          v-model="form.valor_importancia_segurada"
          label-text="Importância Segurada (Nova)"
          :disabled="disableForm"
        />
      </v-col>
      <v-col
        cols="12"
        sm="4"
        class="pr-sm-5"
        v-if="enableInputsTypeEffectEndTermDontRequired"
      >
        <BaseFormInput
          id-element="newEndTermEndorsement"
          placeholder="DD/MM/YYYY"
          v-model="form.novo_fim_vigencia"
          :rules="[dateTermValid, newDateTermLimit]"
          label-text="Término de Vigência (Novo)"
          v-facade="maskDate"
          :disabled="disableForm"
        />
      </v-col>
    </v-row>
    <v-row class="pb-8" v-if="!isRequestEndorsement">
      <v-col cols="12">
        <p class="primary--text form__title mb-5">
          PRAZOS DE EMISSÃO DO ENDOSSO
        </p>
      </v-col>
      <v-col cols="12" sm="4" md="3" class="pr-sm-5">
        <BaseFormInput
          id-element="emissionEndorsement"
          placeholder="DD/MM/YYYY"
          v-model="form.emitida_em"
          required
          :rules="[requiredInput, dateTermValid]"
          validate-on-blur
          label-text="Emissão"
          v-facade="maskDate"
          :disabled="disableForm"
        />
      </v-col>
      <v-col cols="12" sm="4" md="3" class="pr-sm-5">
        <BaseFormInput
          id-element="startDateTermEndorsement"
          placeholder="DD/MM/YYYY"
          v-model="form.inicio_vigencia"
          required
          :rules="[requiredInput, dateTermValid]"
          validate-on-blur
          label-text="Início de Vigência"
          v-facade="maskDate"
          :disabled="disableForm"
        />
      </v-col>
      <v-col
        cols="12"
        sm="4"
        md="3"
        v-if="
          !enableInputsTypeEffectEndTermDontRequired &&
            !enableInputsTypeEffectEndTerm
        "
      >
        <BaseFormInput
          id-element="endDateTermEndorsement"
          placeholder="DD/MM/YYYY"
          v-model="form.fim_vigencia"
          required
          disabled
          :rules="[requiredInput, dateTermValid]"
          label-text="Término de Vigência"
          v-facade="maskDate"
        />
      </v-col>
      <v-col cols="12" sm="4" md="3" v-else>
        <BaseFormInput
          id-element="endDateTermEndorsement"
          placeholder="DD/MM/YYYY"
          v-model="form.novo_fim_vigencia"
          required
          disabled
          :rules="[requiredInput, dateTermValid]"
          label-text="Término de Vigência"
          v-facade="maskDate"
        />
      </v-col>
    </v-row>
    <div class="pb-8">
      <v-divider />
    </div>
    <!-- Campos que variam conforme tipo de endosso -->
    <v-row class="pb-8" v-if="enableInsuredForm">
      <v-col cols="12">
        <p class="primary--text form__title mb-5">
          SEGURADO/OBJETO
        </p>
      </v-col>
      <v-col cols="12" sm="4" class="pr-sm-5">
        <BaseFormSelect
          id-element="typeInsuredEndorsement"
          placeholder="Selecionar"
          v-model="form.segurado.tipo_pessoa"
          :items="listType"
          required
          :rules="[requiredInput]"
          label-text="Tipo Segurado"
          item-text="nome"
          item-value="id"
          :return-object="true"
          :disabled="disableForm"
        />
      </v-col>
      <v-col cols="12" sm="4" class="pr-sm-5">
        <BaseFormAutocomplete
          id-element="cpfPolicy"
          placeholder="Inserir"
          v-model="form.segurado.numero_documento"
          :items="listInsured"
          required
          :rules="validTypeDocumentAccordingOptionsPolicy"
          validate-on-blur
          label-text="CPF/CNPJ"
          @keyup="searchInput"
          item-text="numero_documento"
          item-value="numero_documento"
          hide-no-data
          :return-object="false"
          :template-insured="true"
          :disabled="disableForm"
        />
      </v-col>
      <v-col cols="12" sm="8" class="pr-sm-5">
        <BaseFormAutocomplete
          id-element="insuredPolicy"
          placeholder="Inserir"
          v-model="form.segurado.nome"
          :items="listInsured"
          required
          :rules="[requiredInput]"
          validate-on-blur
          label-text="Segurado"
          item-text="nome"
          item-value="nome"
          @keyup="searchInput"
          hide-no-data
          :return-object="false"
          :template-insured="true"
          :disabled="disableForm"
        />
      </v-col>
      <v-col cols="12" sm="3" class="pb-5 pb-sm-0 pt-sm-8">
        <BaseButton
          class="secondary"
          width="100%"
          @click="openModalAddress"
          v-html="'ENDEREÇO'"
        />
      </v-col>
      <fragment v-if="isJudiciaryProcessNumberRequired">
        <v-col cols="12" sm="4" class="pr-sm-5" v-if="isTypeProcessRequired">
          <BaseFormSelect
            id-element="typeProcessEndorsement"
            placeholder="Selecionar"
            v-model="form.processo.tipo_processo"
            :items="listEmissionType"
            item-text="nome"
            item-value="id"
            :return-object="true"
            required
            :rules="[requiredInput]"
            label-text="Tipo de Processo"
            :disabled="disableForm"
          />
        </v-col>
        <v-col cols="12" sm="8" v-if="isEletronicProcess">
          <BaseFormInput
            id-element="numberProcessEndorsement"
            placeholder="Inserir"
            v-model="form.processo.numero_processo"
            required
            :rules="[requiredInput, numberProcessValid]"
            validate-on-blur
            v-facade="maskNumberProcess"
            label-text="N° Processo Judicial"
            :disabled="disableForm"
          />
        </v-col>
        <v-col cols="12" sm="8" v-else>
          <BaseFormInput
            id-element="numberProcessEndorsement"
            placeholder="Inserir"
            v-model="form.processo.numero_processo"
            required
            :rules="[requiredInput, maxLengthPhysicalProcess]"
            validate-on-blur
            label-text="N° Processo Judicial"
            :disabled="disableForm"
          />
        </v-col>
        <v-col cols="12" sm="6" class="pr-sm-5" v-if="hasSelectableCourt">
          <BaseFormSelect
            id-element="courtEndorsement"
            placeholder="Selecionar"
            v-model="form.processo.tribunal"
            item-text="nome"
            item-value="id"
            :return-object="true"
            :items="listJusticeRegion"
            required
            :rules="[requiredInput]"
            :label-text="nameLabelCourt"
            :disabled="disableForm"
          />
        </v-col>
        <v-col cols="12" sm="6" class="pr-md-5" v-else>
          <BaseFormInput
            id-element="justiceRegionEditQuotation"
            placeholder="Digite"
            v-model="formProcessoNomeTribunal"
            required
            :rules="[requiredInput]"
            :label-text="nameLabelCourt"
          />
        </v-col>
        <v-col cols="12" sm="6" v-if="hasSelectableCourt">
          <BaseFormSelect
            id-element="regionCourtEndorsement"
            placeholder="Selecionar"
            v-model="form.processo.unidade"
            item-text="nome"
            item-value="id"
            :return-object="true"
            :items="listCourtJustice"
            required
            :rules="[requiredInput]"
            :label-text="nameLabelJusticeRegion"
            :disabled="disableForm"
          />
        </v-col>
        <v-col cols="12" sm="6" v-else>
          <BaseFormInput
            id-element="courtJusticeEditQuotation"
            placeholder="Digite"
            v-model="formProcessoNomeUnidade"
            required
            :rules="[requiredInput]"
            :label-text="nameLabelJusticeRegion"
          />
        </v-col>
      </fragment>
      <v-col cols="12" sm="6" class="pr-sm-5">
        <BaseFormInput
          id-element="numberClientEndorsement"
          placeholder="Inserir"
          v-model="form.numero_controle_interno"
          validate-on-blur
          label-text="N° Interno Cliente"
          disabled
        />
      </v-col>
      <v-col cols="12" sm="4" md="3" class="pb-5 pb-sm-0 pt-sm-8">
        <BaseButton
          class="secondary"
          v-html="'OBJETO DO SEGURO'"
          width="100%"
          @click="openModalInsuranceObject"
        />
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
    </v-row>
    <!-- Campos fixos independente do tipo de endosso -->
    <PolicyFormSection
      :form="form"
      :error-messages="errorMessagesEndorsement"
      name-document="Anexar Endosso"
      :error-messages-bills="errorMessagesBills"
      is-endorsement
      :is-request-endorsement="isRequestEndorsement"
      :disable-form="disableForm"
      :current-policy="currentPolicy"
    />
    <BaseFooter>
      <v-row>
        <v-spacer />
        <fragment v-if="showButtons">
          <v-col cols="12" sm="5" md="3" xl="2" class="pr-sm-5 pb-5 pb-sm-0">
            <BaseButton
              v-html="'CANCELAR'"
              class="pink white--text"
              width="100%"
              @click="$emit('cancel')"
            />
          </v-col>
          <v-col cols="12" sm="5" md="3" xl="2">
            <BaseButton
              v-html="nameButton"
              class="secondary"
              width="100%"
              @click="submit"
            />
          </v-col>
        </fragment>
        <v-col cols="12" sm="5" md="3" v-if="canRequestCancel">
          <BaseButton
            v-html="'CANCELAR ENDOSSO'"
            class="pink white--text"
            width="100%"
            :to="{
              name: 'cancel endorsement policy',
              params: { id: $route.params.id, numero: form.numero }
            }"
          />
        </v-col>
        <v-col
          cols="12"
          sm="5"
          md="3"
          class="pr-sm-5 pb-5 pb-sm-0"
          v-if="canReproveRequest"
        >
          <BaseButton
            v-html="'REPROVAR SOLICITAÇÃO'"
            class="pink white--text"
            width="100%"
            @click="$emit('reprove')"
          />
        </v-col>
        <v-col cols="12" sm="5" md="3" v-if="canApproveRequest">
          <BaseButton
            v-html="'APROVAR SOLICITAÇÃO'"
            class="secondary"
            width="100%"
            @click="$emit('approve')"
          />
        </v-col>
      </v-row>
    </BaseFooter>
    <ModalAddressForm
      v-model="modalAddress"
      :form="form.segurado"
      @close="closeModalAddress"
      @submit="closeModalAddress"
    />
    <ModalInsuranceObject
      v-model="modalInsuranceObject"
      :form="form"
      @close="closeModalInsuranceObject"
      @submit="closeModalInsuranceObject"
    />
  </v-form>
</template>
<script>
const mockEmissionType = [
  {
    nome: "PROCESSO FÍSICO",
    id: "fisico"
  },
  {
    nome: "PROCESSO ELETRÔNICO",
    id: "eletronico"
  }
];
const mockListType = [
  {
    id: "pessoa_fisica",
    nome: "PESSOA FÍSICA"
  },
  {
    id: "pessoa_juridica",
    nome: "PESSOA JURÍDICA"
  }
];
const TABLE_HEADERS_CRITICISM = [
  { text: "Crítica", value: "critica", sortable: false },
  { text: "Chat", value: "chat", sortable: false },
  { text: "Ações", value: "acoes", sortable: false }
];
const SEGMENT_ID = "justica_trabalho";
import {
  requiredInput,
  dateTermValid,
  maxLengthTextArea,
  numberProcessValid,
  maxLengthPhysicalProcess,
  isValidCpfOrCnpj,
  documentNumberMatchesType
} from "@/utils/rules.js";
import { maskDate, maskNumberProcess } from "@/utils/masks.js";
import PolicyFormSection from "@/components/form/PolicyFormSection";
import ModalAddressForm from "../components/ModalAddressForm";
import ModalInsuranceObject from "../components/ModalInsuranceObject";
import { mapActions, mapState } from "vuex";
import { LoadingRequest } from "@/mixins/LoadingMixin";
import { formatDateToDDMMYYYY, asMoneyString } from "@/utils/formats.js";
import _ from "lodash";
import { differenceInCalendarDays } from "date-fns";
import { formatIsoDateToDDMMYYYY } from "@/utils/formats.js";
import LabelInputMixin from "@/mixins/LabelInputMixin";
export default {
  data: () => ({
    requiredInput,
    dateTermValid,
    maskDate,
    errorMessagesEndorsement: [],
    errorMessagesBills: [],
    modalAddress: false,
    modalInsuranceObject: false,
    listEmissionType: mockEmissionType,
    listType: mockListType,
    timeout: null,
    formatDateToDDMMYYYY,
    asMoneyString,
    cancelReason: {
      id: "00000000-0000-0000-0000-000000000000",
      descricao: "OUTRO"
    },
    labelCancelInput: "Informe o outro motivo de cancelamento",
    headerCriticism: TABLE_HEADERS_CRITICISM,
    maxLengthTextArea,
    maskNumberProcess,
    numberProcessValid,
    maxLengthPhysicalProcess,
    formatIsoDateToDDMMYYYY,
    isValidCpfOrCnpj,
    formTipo: { type: Object }
  }),
  components: {
    PolicyFormSection,
    ModalAddressForm,
    ModalInsuranceObject
  },
  mixins: [LoadingRequest, LabelInputMixin],
  created() {
    if (this.form) {
      this.formTipo = this.form.tipo;
    }

    this.fetchCancelItems();
    this.isElectronic ? this.fetchNumberProcess() : this.fetchJusticeRegion();
    this.updateValues();
  },
  computed: {
    ...mapState("policy", [
      "listTypeEndorsement",
      "currentPolicy",
      "listInsured",
      "cancelItems",
      "itemsEndorsement",
      "currentRequestEndorsement"
    ]),
    ...mapState("quotation", [
      "listCourtJustice",
      "listJusticeRegion",
      "eletronicProcessValue"
    ]),
    textTooltip() {
      return `Cancelamento solicitado por
        ${this.form.cancelamento_solicitado_por.nome} em
        ${this.formatIsoDateToDDMMYYYY(this.form.cancelamento_solicitado_em)}`;
    },
    origemCadastroPlaceholder() {
      if (!this.form.origem_cadastro) {
        return "Inserir";
      }

      return this.form.origem_cadastro.name;
    },
    enableCreationDate() {
      return (this.currentPolicy.solicitacoes_endossos != null
      && this.currentPolicy.solicitacoes_endossos.length > 0       
      && this.currentPolicy.solicitacoes_endossos[0].criada_em != null);
    },
    colorCriticismStatus() {
      return (
        {
          sem_criticas: "toolbar-timeline__background-white",
          pendente_regularizacao: "lightYellow",
          regularizado: "green"
        }[this.currentRequestEndorsement.status_critica.id] || "pink"
      );
    },
    enableInsuredForm() {
      return (
        this.formTipo &&
        [
          "correcao_dados",
          "alterar_cobertura",
          "aniversario_apolice",
          "alteracoes_diversas"
        ].includes(this.formTipo.id)
      );
    },
    enableInputsTypeEffectInsuredAmount() {
      return (
        this.formTipo &&
        [
          "importancia_segurada",
          "prorrogar_vigencia_importancia_segurada"
        ].includes(this.formTipo.id)
      );
    },
    enableInputsTypeEffectInsuredAmountDontRequired() {
      return (
        this.formTipo &&
        [
          "alterar_cobertura",
          "aniversario_apolice",
          "alteracoes_diversas"
        ].includes(this.formTipo.id)
      );
    },
    enableInputsTypeEffectEndTerm() {
      return (
        this.formTipo &&
        [
          "alterar_termino_vigencia",
          "prorrogar_vigencia_importancia_segurada"
        ].includes(this.formTipo.id)
      );
    },
    enableInputsTypeEffectEndTermDontRequired() {
      return this.formTipo && this.formTipo.id === "alteracoes_diversas";
    },
    enableInputMoreInfo() {
      return (
        this.formTipo &&
        [
          "cancelamento_apolice",
          "cancelamento_endosso",
          "cancelamento_remissao",
          "baixa_apolice"
        ].includes(this.formTipo.id)
      );
    },
    enableInputsCancel() {
      return (
        this.formTipo &&
        [
          "cancelamento_apolice",
          "cancelamento_endosso",
          "cancelamento_remissao"
        ].includes(this.formTipo.id)
      );
    },
    enableInputNumberCancelEndorsement() {
      return this.endorsementKindIs("cancelamento_endosso") && this.isCreate;
    },
    disableInputNumberCancelEndorsement() {
      return this.endorsementKindIs("cancelamento_endosso") && !this.isCreate;
    },
    enableInputsEndEndorsement() {
      return this.endorsementKindIs("baixa_apolice");
    },
    enableInputEditCoverage() {
      return this.endorsementKindIs("alterar_cobertura");
    },
    isElectronic() {
      return this.form.processo?.tipo_processo?.id == "eletronico";
    },
    validatePayment() {
      return (
        !this.isRequestEndorsement &&
        !this.form.sem_movimento_premio &&
        this.form.forma_pagamento?.id == "boleto" &&
        this.form.fator_premio?.id == "pagamento"
      );
    },
    showButtons() {
      return this.isEdit || this.isCreate;
    },
    disableForm() {
      return !this.isEdit && !this.isCreate;
    },
    listCancelItems() {
      let items = _.cloneDeep(this.cancelItems);
      items.push(this.cancelReason);
      return items;
    },
    enableOtherReasons() {
      return (
        this.form.motivo_cancelamento?.id === this.cancelReason.id &&
        this.formTipo &&
        [
          "cancelamento_apolice",
          "cancelamento_remissao",
          "cancelamento_endosso"
        ].includes(this.formTipo.id)
      );
    },
    isEletronicProcess() {
      return this.form.processo?.tipo_processo == "eletronico";
    },
    showCriticism() {
      return (
        this.isRequestEndorsement &&
        this.currentRequestEndorsement.status_critica &&
        !this.isCreate
      );
    },
    isModalityJudicial() {
      return this.currentPolicy.modalidade.id === "judicial";
    },
    isSubmodalityAdministrative() {
      return this.currentPolicy.submodalidade.id === "administrativa";
    },
    paddingInputMoreInfo() {
      return (this.enableInputNumberCancelEndorsement ||
        this.disableInputNumberCancelEndorsement) &&
        this.isRequestEndorsement
        ? "pl-sm-5"
        : !this.isRequestEndorsement
        ? "pr-sm-5"
        : "";
    },
    validTypeDocumentAccordingOptionsPolicy() {
      return this.currentPolicy.opcoes?.segurado[
        this.form.segurado.tipo_pessoa?.id
      ]?.documento.obrigatorio
        ? [this.requiredInput, this.isValidCpfOrCnpj, this.ruleSameType]
        : [];
    },
    ruleSameType() {
      return documentNumberMatchesType(
        this.form.segurado.tipo_pessoa?.id,
        this.form.segurado.numero_documento
      );
    },
    isTypeProcessRequired() {
      return this.currentPolicy.opcoes?.processo_judiciario?.tipo_processo
        .obrigatorio;
    },
    hasSelectableCourt() {
      return !this.currentPolicy.opcoes?.processo_judiciario?.tribunal
        ?.nome_livre;
    },
    isJudiciaryProcessNumberRequired() {
      return this.currentPolicy.opcoes?.processo_judiciario?.numero_processo
        ?.obrigatorio;
    },
    formProcessoNomeUnidade() {
      if (
        this.form == null ||
        this.form.processo == null ||
        this.form.processo.nome_unidade == null
      ) {
        return "N/A";
      }

      return this.form.processo.nome_unidade;
    },
    formProcessoNomeTribunal() {
      if (
        this.form == null ||
        this.form.processo == null ||
        this.form.processo.nome_tribunal == null
      ) {
        return "N/A";
      }

      return this.form.processo.nome_tribunal;
    }
  },
  methods: {
    ...mapActions("policy", [
      "requestInsuredByNameOrNumber",
      "requestCancelItems"
    ]),
    ...mapActions("quotation", [
      "requestNumberProcess",
      "requestCourtJustice",
      "requestJusticeRegion"
    ]),
    ...mapActions(["showAlertNotification"]),
    fetchCancelItems() {
      this.withLoading(
        this.requestCancelItems(this.currentPolicy.modalidade.id)
      );
    },
    endorsementKindIs(item) {
      return this.formTipo?.id === item;
    },
    newDateTermLimit() {
      if (!this.formTipo_efeito_termino || !this.form.novo_fim_vigencia)
        return true;
      const [dayNew, monthNew, yearNew] = this.form.novo_fim_vigencia.split(
        "/"
      );
      const [day, month, year] = this.form.fim_vigencia.split("/");
      const newDateTerm = new Date(`${yearNew}-${monthNew}-${dayNew}`);
      const dateTerm = new Date(`${year}-${month}-${day}`);
      const result = differenceInCalendarDays(newDateTerm, dateTerm);
      const type =
        this.formTipo_efeito_termino.id == "prorrogacao_vigencia"
          ? "maior"
          : "menor";
      return (
        (type == "maior" && result >= 0) ||
        (type == "menor" && result <= 0) ||
        `Novo Término de vigência deve ser ${type} que data de Término em prazos`
      );
    },
    dateTermLimit() {
      if (!this.formTipo_efeito_termino || !this.form.novo_fim_vigencia)
        return true;
      const [dayNew, monthNew, yearNew] = this.form.novo_fim_vigencia.split(
        "/"
      );
      const [day, month, year] = this.form.fim_vigencia.split("/");
      const newDateTerm = new Date(`${yearNew}-${monthNew}-${dayNew}`);
      const dateTerm = new Date(`${year}-${month}-${day}`);
      const result = differenceInCalendarDays(dateTerm, newDateTerm);
      const type =
        this.formTipo_efeito_termino.id == "prorrogacao_vigencia"
          ? "menor"
          : "maior";
      return (
        (type == "maior" && result >= 0) ||
        (type == "menor" && result <= 0) ||
        `Término de vigência deve ser ${type} que data de Novo Término`
      );
    },
    searchInput(value) {
      clearTimeout(this.timeout);
      let vm = this;
      // fazer requisição apenas se tiver valor no input
      if (value.target.value.length >= 3)
        this.timeout = setTimeout(function() {
          const payload = `?query=${value.target.value}`;
          vm.fetchInsured(payload);
          vm.timeout = null;
        }, 500);
    },
    fetchInsured(payload) {
      this.withLoading(this.requestInsuredByNameOrNumber(payload));
    },
    validate() {
      return (
        this.$refs.form.validate() &&
        this.validateEndorsement() &&
        this.validateBills() &&
        this.validateInstallments()
      );
    },
    fetchNumberProcess() {
      this.withLoading(
        this.requestNumberProcess(this.getPayloadNumberProcess())
      );
    },
    getPayloadNumberProcess() {
      return {
        id: SEGMENT_ID,
        number: this.form.processo.numero_processo
      };
    },
    fetchCourtJustice() {
      this.withLoading(this.requestCourtJustice(this.getPayloadCourtJustice()));
    },
    getPayloadCourtJustice() {
      return {
        segmentID: SEGMENT_ID,
        justiceID: this.form.processo.tribunal.id
      };
    },
    validateEndorsement() {
      this.errorMessagesEndorsement =
        this.form.apolice.length == 0 ? ["É preciso anexar o endosso"] : [];
      return this.isRequestEndorsement || this.form.apolice.length > 0;
    },
    validateBills() {
      this.errorMessagesBills =
        this.form.boletos.length == 0 && this.validatePayment
          ? ["É preciso anexar o boleto"]
          : [];
      return this.validatePayment ? this.form.boletos.length > 0 : true;
    },
    validateInstallments() {
      // Caso o valor do premio dê diferente das parcelas, ou o valor_premio
      // foi alterado e precisa ser refeito o cálculo do premio ou não foi
      // feito nenhuma vez o cálculo do prêmio e precisa ser feito.
      // No caso do endosso tem o ponto que precisa ter uma movimentação
      // de prêmio, caso não tenha e seja o tipo restituição, não precisa validar
      const validate =
        !this.form.sem_movimento_premio &&
        this.form.fator_premio?.id == "pagamento"
          ? _.round(this.form.valor_premio, 1) ===
            _.round(_.sumBy(this.form.parcelas, "valor"), 1)
          : true;
      if (!validate) {
        this.showAlertNotification({
          message:
            "É preciso calcular/recalcular o prêmio antes de finalizar processo.",
          type: "error"
        });
      }
      return validate;
    },
    submit() {
      if (!this.validate()) return;
      this.$emit("submit", this.form);
    },
    closeModalAddress() {
      this.modalAddress = false;
    },
    openModalAddress() {
      this.modalAddress = true;
    },
    closeModalInsuranceObject() {
      this.modalInsuranceObject = false;
    },
    openModalInsuranceObject() {
      this.modalInsuranceObject = true;
    },
    autocompleteFormAccordingInput(value, input) {
      // Se selecionar um elemento do back-end, deve-se autocompletar
      // a tabela inteira conforme o elemente selecionado
      const item = this.listInsured.find(x => x[input] == value);
      if (this.listInsured.length >= 1) {
        const newForm = _.pick(item, [
          "tipo_pessoa",
          "numero_documento",
          "endereco",
          "nome"
        ]);
        this.form.segurado = this.mergeProperties(this.form.segurado, newForm);
        // mergeProperties não resolve para objetos com valores nulos
        // forcarei o merge aqui, e quando resolver o problema
        // na funcao mergeProperties eu deleto essa parte
        this.form.segurado.tipo_pessoa = _.cloneDeep(item.tipo_pessoa);
        this.form.segurado.endereco.uf = _.cloneDeep(item.endereco.uf);
      }
    },
    fetchJusticeRegion() {
      if (this.form.processo)
        this.withLoading(this.requestJusticeRegion(SEGMENT_ID));
    },
    redirectCriticism() {
      this.$router.push({
        name: "criticism endorsement policy",
        params: {
          id: this.$route.params.id,
          endosso: this.$route.params.endosso
        }
      });
    },
    updateValues() {
      if (this.isSubmodalityAdministrative) {
        // Para submodalidade administrativa, os campos de nome_unidade e nome_tribunal
        // devem vir definidos como padrão por N/A, pois nem sempre existem estes campos
        // pois como o campo é obrigatório e eles não querem tirar ela apenas para
        // esta submodalidade, então cria estes valores default no caso de não ter valor.
        if (this.form != null && this.form.processo != null) {
          this.form.processo.nome_unidade = this.formProcessoNomeUnidade;
          this.form.processo.nome_tribunal = this.formProcessoNomeTribunal;
        }
      }
    }
  },
  props: {
    form: {
      type: Object
    },
    nameButton: {
      type: String
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    isRequestEndorsement: {
      type: Boolean,
      default: false
    },
    disableTypeEndorsement: {
      type: Boolean,
      default: false
    },
    canRequestCancel: {
      type: Boolean,
      default: false
    },
    isCreate: {
      type: Boolean,
      default: false
    },
    canApproveRequest: {
      type: Boolean,
      default: false
    },
    canReproveRequest: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    formTipo(value) {
      this.form.tipo = value;
    },
    "formTipo.id"(value) {
      if (
        [
          "alterar_cobertura",
          "aniversario_apolice",
          "alteracoes_diversas"
        ].includes(value)
      ) {
        this.form.novo_fim_vigencia = this.formatDateToDDMMYYYY(
          this.currentPolicy.fim_vigencia
        );
        this.form.valor_importancia_segurada = _.clone(
          this.currentPolicy.valor_importancia_segurada
        );
      }
    },
    "form.segurado.numero_documento"(value) {
      this.autocompleteFormAccordingInput(value, "numero_documento");
    },
    "form.segurado.nome"(value) {
      this.autocompleteFormAccordingInput(value, "nome");
    },
    "form.processo.tipo_processo.id"(value) {
      if (value == "fisico") this.fetchJusticeRegion();
    },
    "form.processo.numero_processo"(value) {
      value &&
        value.length == 25 &&
        this.form.processo.tipo_processo.id == "eletronico" &&
        this.fetchNumberProcess();
    },
    "form.processo.tribunal.nome"(value) {
      if (value) this.fetchCourtJustice();
    },
    eletronicProcessValue(value) {
      if (this.form.processo.tipo_processo.id == "eletronico") {
        this.form.processo.unidade = value.unidade;
        this.form.processo.tribunal = value.tribunal;
      }
    },
    cancelItems(item) {
      if (item && item.length == 0) {
        this.formTipo = _.cloneDeep(this.cancelReason);
        this.labelCancelInput = "Motivo de cancelamento";
      }
    }
  }
};
</script>
<style lang="sass">
.endorsement-form__disclaimer
  border-radius: 4px
</style>
